import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { comics, categories } from '@/mocks/comics';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === Number(id));
  const [activeTab, setActiveTab] = useState<'info' | 'chapters'>('info');

  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveTab('info');
  }, [id]);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-24 text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <h2 className="text-lg font-heading font-bold text-foreground-900 mb-2">漫画未找到</h2>
            <p className="text-sm text-foreground-500 mb-6">该漫画可能已下架或不存在</p>
            <Link to="/" className="inline-flex px-5 py-2 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const catInfo = categories.find((c) => c.id === comic.category);
  const catName = catInfo?.name || comic.category;

  const relatedComics = comics
    .filter((c) => c.id !== comic.id && c.category === comic.category)
    .slice(0, 6);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: catName, href: `/category/${comic.category}` },
    { label: comic.title },
  ];

  const chaptersPerPage = 20;
  const totalPages = Math.ceil(comic.chapters / chaptersPerPage);
  const chapterList = Array.from({ length: Math.min(comic.chapters, chaptersPerPage) }, (_, i) => ({
    number: comic.chapters - i,
    title: `第${comic.chapters - i}话`,
    date: new Date(comic.updatedAt).getTime() - i * 7 * 24 * 60 * 60 * 1000,
  }));

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩漫之家`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: '2024-01-01',
    dateModified: comic.updatedAt,
    publisher: {
      '@type': 'Organization',
      name: '韩漫之家',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            <div className="flex flex-col md:flex-row gap-8 mb-12">
              <div className="w-full md:w-[280px] flex-shrink-0">
                <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - ${catName} 韩国漫画`}
                    className="w-full h-full object-cover object-top"
                    title={`${comic.title} - 韩漫之家 韩国漫画大全`}
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3">
                  {comic.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-sm text-foreground-600">作者：<strong>{comic.author}</strong></span>
                  <span className="text-sm text-foreground-400">|</span>
                  <span className="text-sm text-foreground-600">状态：<strong>{comic.status}</strong></span>
                  <span className="text-sm text-foreground-400">|</span>
                  <span className="flex items-center gap-1 text-sm">
                    <i className="ri-star-fill text-amber-400 text-sm"></i>
                    <strong className="text-foreground-900">{comic.rating}</strong>
                    <span className="text-foreground-500">分</span>
                  </span>
                  <span className="text-sm text-foreground-400">|</span>
                  <span className="text-sm text-foreground-600">{comic.views}次阅读</span>
                </div>

                <div className="flex flex-wrap items-center gap-2 mb-6">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${catInfo?.color || 'bg-background-100 text-foreground-600'}`}>
                    {catName}
                  </span>
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-2 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="flex items-center gap-2 mb-6">
                  <button className="px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2">
                    <i className="ri-play-circle-line"></i> 从第1话开始
                  </button>
                  <button className="px-6 py-2.5 bg-secondary-100 text-secondary-700 text-sm font-medium rounded-full hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2">
                    <i className="ri-bookmark-line"></i> 收藏
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 transition-colors cursor-pointer">
                    <i className="ri-share-forward-line text-sm text-foreground-600"></i>
                  </button>
                </div>

                <div className="flex items-center gap-4 bg-background-100 rounded-lg px-4 py-3 mb-6">
                  <div className="text-center">
                    <div className="text-lg font-heading font-bold text-primary-500">{comic.chapters}</div>
                    <div className="text-xs text-foreground-500">总章节</div>
                  </div>
                  <div className="w-px h-8 bg-background-300"></div>
                  <div className="text-center">
                    <div className="text-lg font-heading font-bold text-foreground-900">{comic.updateTo}</div>
                    <div className="text-xs text-foreground-500">最新话</div>
                  </div>
                  <div className="w-px h-8 bg-background-300"></div>
                  <div className="text-center">
                    <div className="text-lg font-heading font-bold text-foreground-900">{comic.updatedAt}</div>
                    <div className="text-xs text-foreground-500">最新更新</div>
                  </div>
                </div>

                <div className="bg-background-100 rounded-lg p-4">
                  <h3 className="text-sm font-heading font-bold text-foreground-900 mb-2">作品简介</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4 mb-6">
              <button
                onClick={() => setActiveTab('info')}
                className={`pb-2 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer border-b-2 ${
                  activeTab === 'info'
                    ? 'border-primary-500 text-primary-500'
                    : 'border-transparent text-foreground-500 hover:text-foreground-700'
                }`}
              >
                作品信息
              </button>
              <button
                onClick={() => setActiveTab('chapters')}
                className={`pb-2 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer border-b-2 ${
                  activeTab === 'chapters'
                    ? 'border-primary-500 text-primary-500'
                    : 'border-transparent text-foreground-500 hover:text-foreground-700'
                }`}
              >
                章节列表 ({comic.chapters}话)
              </button>
            </div>

            {activeTab === 'chapters' && (
              <div className="mb-16">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 mb-6">
                  {chapterList.map((ch, i) => (
                    <div
                      key={ch.number}
                      className="flex items-center justify-between p-3 bg-background-100 rounded-md hover:bg-background-200/70 transition-colors cursor-pointer"
                    >
                      <span className="text-sm text-foreground-700 whitespace-nowrap">
                        第{ch.number}话
                        {i === 0 && (
                          <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-accent-100 text-accent-700 rounded">NEW</span>
                        )}
                      </span>
                      <span className="text-xs text-foreground-400 whitespace-nowrap">
                        {new Date(ch.date).toLocaleDateString('zh-CN')}
                      </span>
                    </div>
                  ))}
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2">
                    <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-background-200 transition-colors cursor-pointer">
                      <i className="ri-arrow-left-s-line text-sm"></i>
                    </button>
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => (
                      <button
                        key={i}
                        className={`w-8 h-8 flex items-center justify-center rounded-full text-sm transition-colors cursor-pointer ${
                          i === 0
                            ? 'bg-primary-500 text-background-50'
                            : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                    {totalPages > 5 && <span className="text-sm text-foreground-400">...</span>}
                    <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-background-200 transition-colors cursor-pointer">
                      <i className="ri-arrow-right-s-line text-sm"></i>
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'info' && (
              <div className="mb-16">
                <div className="bg-background-100 rounded-lg p-6">
                  <h3 className="text-base font-heading font-bold text-foreground-900 mb-4">详细信息</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex gap-2">
                      <span className="text-sm text-foreground-500 whitespace-nowrap">作品名称：</span>
                      <span className="text-sm text-foreground-800">{comic.title}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-sm text-foreground-500 whitespace-nowrap">作者：</span>
                      <span className="text-sm text-foreground-800">{comic.author}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-sm text-foreground-500 whitespace-nowrap">分类：</span>
                      <span className="text-sm text-foreground-800">{catName}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-sm text-foreground-500 whitespace-nowrap">状态：</span>
                      <span className="text-sm text-foreground-800">{comic.status}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-sm text-foreground-500 whitespace-nowrap">总章节：</span>
                      <span className="text-sm text-foreground-800">{comic.chapters}话</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-sm text-foreground-500 whitespace-nowrap">评分：</span>
                      <span className="text-sm text-foreground-800">{comic.rating}分</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-sm text-foreground-500 whitespace-nowrap">阅读量：</span>
                      <span className="text-sm text-foreground-800">{comic.views}次</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-sm text-foreground-500 whitespace-nowrap">最后更新：</span>
                      <span className="text-sm text-foreground-800">{comic.updatedAt}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {relatedComics.length > 0 && (
              <div className="pb-16">
                <h2 className="text-lg font-heading font-bold text-foreground-900 mb-6">相关推荐</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4" data-product-shop>
                  {relatedComics.map((c) => (
                    <Link
                      key={c.id}
                      to={`/comic/${c.id}`}
                      className="group cursor-pointer"
                    >
                      <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-200">
                        <img
                          src={c.cover}
                          alt={`${c.title} - 相关推荐漫画`}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                          <div className="flex items-center gap-1">
                            <i className="ri-star-fill text-amber-400 text-[10px]"></i>
                            <span className="text-[10px] text-background-50">{c.rating}</span>
                          </div>
                        </div>
                      </div>
                      <h3 className="text-xs font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                        {c.title}
                      </h3>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}